import React from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendOrder,
	FrontendProductOrder,
	FrontendUserInfo,
} from "node-library/dist/portal";

import { OrderStatus, Severity, UserType } from "node-library/dist/constants";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";
import { roleMeetsRequirement } from "node-library/dist/helpers/request_helper";

import { NeutralColor } from "node-library/dist/colors";

import { Card, Tooltip } from "@mui/material";
import { useSnackbar } from "../../../../../common-components/toast/showToastMessage";
import {
	getAdminStatusColors,
	getAdminStatusString,
	getStatusColors,
	getStatusString,
} from "../../../../../helpers/orderHelper";
import {
	AlertIcon,
	COAIcon,
	DeleteIcon,
	EditIcon,
	UploadIcon,
	ViewResultIcon,
} from "../../../../../common-components/svgIcons";
import { KebabDropdownButton } from "../../../../../common-components/button";

interface Props {
	order: FrontendProductOrder;
	user: FrontendUserInfo | undefined;
	goToViewOrderDetails: () => void;
	viewOrderCOC: () => void;
	goToEditOrderPage: () => void;

	goToOrderReportPage: () => void;

	deleteOrder: () => void;
	// editOrder: () => void;
}

export default function AdminOrderCell(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		order,
		goToViewOrderDetails,
		user,
		viewOrderCOC,
		goToOrderReportPage,
		goToEditOrderPage,
		deleteOrder,
	} = props;

	const statusString: string = getAdminStatusString(
		order.orderStatus,
		order.orderViewedByAdmin,
		translation
	);
	const statusColor: {
		backgroundColor: string;
		borderColor: string;
		textColor: string;
	} = getAdminStatusColors(
		order.orderStatus,
		order.orderViewedByAdmin,
		order.paid
	);

	let actionOptions: Array<{
		key: string;
		onClick: () => void;
		children: React.ReactNode;
	}>;

	actionOptions = [
		{
			key: `edit-batch-${order.orderId}`,
			onClick: () => {
				goToEditOrderPage();
			},
			children: (
				<div className="icon-dropdown-menu-item">
					<EditIcon strokeColor={NeutralColor.NEUTRAL_GREY_900} />
					{translation("Edit")}
				</div>
			),
		},

		{
			key: `view-coc-order-${order.orderId}`,
			onClick: () => {
				viewOrderCOC();
			},
			children: (
				<div className="icon-dropdown-menu-item">
					<COAIcon />
					{translation("ViewChainOfCustody")}
				</div>
			),
		},
		{
			key: `delete-order-${order.orderId}`,
			onClick: () => {
				deleteOrder();
			},
			children: (
				<div className="icon-dropdown-menu-item">
					<DeleteIcon />
					{translation("Delete")}
				</div>
			),
		},
		...([
			OrderStatus.IN_PROGRESS,
			// OrderStatus.READY_TO_SHIP,
			OrderStatus.SHIPPED,
			OrderStatus.DELIVERED,
		].includes(order.orderStatus)
			? [
					{
						key: `view-result-order-${order.orderId}`,
						onClick: () => {
							goToOrderReportPage();
						},
						children: (
							<div className="icon-dropdown-menu-item">
								<UploadIcon size={20} />
								{translation("UploadReports")}
							</div>
						),
					},
			  ]
			: []),
	];

	const orderHeaderRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: "5px",
			}}
		>
			<div
				className="typography-caption sample-status-box"
				style={{
					backgroundColor: statusColor.backgroundColor,
					border: `1px solid ${statusColor.borderColor}`,
					color: statusColor.textColor,
				}}
			>
				{statusString}
			</div>

			<KebabDropdownButton
				menuOptions={actionOptions}
				id={`batch-more-action-menu-${order.orderId}`}
				onClick={(e) => {}}
			></KebabDropdownButton>
		</div>
	);

	let licenseInfo: React.ReactNode;
	if (order.orderStatus === OrderStatus.INCOMPLETE && order.orderId) {
		licenseInfo = (
			<Tooltip
				PopperProps={{
					sx: {
						"& .MuiTooltip-tooltip": {
							backgroundColor: NeutralColor.NEUTRAL_GREY_900,
							padding: "12px 16px",
						},
					},
				}}
				title={translation("LicenseDeletedError")}
				arrow
			>
				<span style={{ display: "flex", width: "fit-content" }}>
					<AlertIcon></AlertIcon>
				</span>
			</Tooltip>
		);
	} else {
		licenseInfo = order.workOrderId || translation("None");
	}
	const orderBody: React.ReactNode = (
		<div
			style={{
				marginTop: "10px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("OrderNumberShort")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.orderId}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("License")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{licenseInfo}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Samples")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.instrumentCount}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Submittedon")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.submitted ? formatCustomDate(order.submitted) : "--"}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Receivedon")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.assigned ? formatCustomDate(order.assigned) : "--"}
				</div>
			</div>
		</div>
	);

	return (
		<Card
			className="card-border-radius-14p card-medium-padding highlight-card"
			style={{ cursor: "pointer" }}
			key={order.orderId}
			onClick={() => {
				goToViewOrderDetails();
			}}
		>
			{orderHeaderRow}
			{orderBody}
		</Card>
	);
}
